import Immutable from 'immutable';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useResize } from 'components/ArtboardLayout/hooks/useResize';
import { useLayoutDragHotspot } from 'components/Section/hooks';
import { ProjectsConfig, ProjectType } from 'const';
import {
  resetLayoutHeightSilently as resetLayoutHeightSilentlyAction,
  setLayoutAndInstances,
} from 'containers/Layouts/actions';
import { Action } from 'containers/Layouts/models';
import * as LayoutsModels from 'containers/Layouts/models';
import { relationsByRegularLayoutId as relationsByRegularLayoutIdSelector } from 'containers/Relations/selectors';
import { getActiveLayer } from 'hooks/useActiveLayer';
import { useImagesLoadingObserver } from 'hooks/useImagesLoadingObserver';
import * as Models from 'models';
import { isImage } from 'utils/entityType';
import { isLayoutScrollable } from 'utils/layouts/isLayoutScrollable';
import { isRegularRelation } from 'utils/relations/isRegularRelation';
import { sum } from 'utils/sum';

type Props = {
  getLayoutsRefs?: () => Immutable.Map<string, HTMLDivElement>;
  isLayoutBeingUploaded: boolean;
  isOverByAllowedItem: boolean;
  groupLayout: Models.GroupLayoutMap;
  ssi: Models.SSIMap | null;
  layouts: Models.CombinedLayoutsMap;
  documents: Models.CombinedDocumentsMap;
  relationsByRegularLayoutId: ReturnType<typeof relationsByRegularLayoutIdSelector>;
  projectType: ProjectType;
};

export const useGroupLayout = (props: Props) => {
  const {
    layouts,
    documents,
    isOverByAllowedItem,
    relationsByRegularLayoutId,
    projectType,
    getLayoutsRefs,
    isLayoutBeingUploaded,
    groupLayout,
    ssi,
  } = props;
  const dispatch = useDispatch();
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    return (): void => {
      isMountedRef.current = false;
    };
  }, []);

  const setLayout: LayoutsModels.ActionCreator.SetLayout = layout => dispatch(setLayoutAndInstances(layout));
  const resetLayoutHeightSilently: LayoutsModels.ActionCreator.ResetLayoutHeightSilently = (id, height) => {
    return dispatch(resetLayoutHeightSilentlyAction(id, height));
  };
  const activeLayer = getActiveLayer();
  const layoutId = groupLayout.get('id');
  const layoutIds = groupLayout.get('layoutIds');
  const childLayouts = layoutIds.map(id => layouts.get(id) as Models.LayoutMap);
  const isGroupLayoutWithOneChild = childLayouts.size === 1;
  const layoutDocument = documents.get(groupLayout.get('documentId')) as Models.GroupLayoutDocumentMap;
  const layoutDocumentName = layoutDocument && layoutDocument.get('name');
  const layoutDocumentNumber = layoutDocument && layoutDocument.get('number');
  const imagesDownloaded = childLayouts
    .flatMap((layout) => {
      const relations = relationsByRegularLayoutId.get(layout.get('id'));

      return relations?.toList() ?? [];
    })
    .filter<Models.LayeredRegularRelationMap>(isRegularRelation)
    .map(relation => documents.get(relation.getIn(['documentId', activeLayer])))
    .filter<Models.ImageMap>(isImage)
    .every(image => !!image.getIn(['_internalInfo', 'source']));

  const container = React.useRef<HTMLDivElement>(null);
  const pinnedLayoutRef = React.useRef<HTMLDivElement>(null);
  const unpinnedLayoutsContainer = React.useRef<HTMLDivElement>(null);

  const { allowScrollableLayout } = ProjectsConfig[projectType];
  const firstLayout = childLayouts.first<Models.LayoutMap>();
  const pinnedLayout = allowScrollableLayout && !isLayoutScrollable(firstLayout) ? firstLayout : null;

  const calcMaxHeight = (): number => pinnedLayout
    ? pinnedLayoutRef.current.offsetHeight + unpinnedLayoutsContainer.current.scrollHeight
    : container.current.scrollHeight;

  const [height, toggleHeight] = useState(groupLayout.getIn(['styles', 'height']));

  useEffect(
    () => toggleHeight(groupLayout.getIn(['styles', 'height'])),
    [groupLayout],
  );

  const { onResize } = useResize({ calcMaxHeight, height, toggleHeight, getClientRect: () => container.current.getBoundingClientRect() });
  const { isDraggingLayout, getLayoutDragHotspotPosition } = useLayoutDragHotspot(getLayoutsRefs, layoutIds, layoutId);

  const onResizeEnd = (): Action.ISetLayout => setLayout(groupLayout.setIn(['styles', 'height'], height));

  const [imagesLoaded, setImagesLoaded] = React.useState(false);
  const onImagesLoaded = (): void => {
    if (isMountedRef.current) {
      setImagesLoaded(true);
    }
  };

  const [fontsLoaded, setFontsLoaded] = React.useState(false);
  React.useEffect(() => {
    void document.fonts.ready.then(() => {
      if (isMountedRef.current) {
        setFontsLoaded(true);
      }
    });
  }, []);

  React.useEffect(
    () => {
      if (ssi || !imagesLoaded || !fontsLoaded) {
        return;
      }

      if (height) {
        const domHeight = calcMaxHeight();

        if (height > domHeight) {
          resetLayoutHeightSilently(layoutId, domHeight);
        }

        return;
      }

      const layoutsRefs = getLayoutsRefs();
      const layoutsHeight = childLayouts.map(layout => layout.getIn(['styles', 'height']) ?? layoutsRefs.get(layout.get('id')).offsetHeight);
      setLayout(groupLayout.setIn(['styles', 'height'], sum(layoutsHeight)));
    },
    [imagesLoaded, fontsLoaded],
  );

  const onDragOver = (event: React.MouseEvent): void => {
    if (isLayoutBeingUploaded || !isOverByAllowedItem || ssi) {
      return;
    }

    getLayoutDragHotspotPosition(event.clientY);
  };

  useImagesLoadingObserver({
    container, onLoaded: () => {
      if (!imagesDownloaded) {
        return;
      }

      onImagesLoaded();
    },
  });

  return {
    pinnedLayout,
    childLayouts,
    isGroupLayoutWithOneChild,
    layoutDocumentName,
    layoutDocumentNumber,
    layoutId,
    layoutIds,
    container,
    pinnedLayoutRef,
    unpinnedLayoutsContainer,

    height,
    onResize,
    onResizeEnd,

    isDraggingLayout,
    onDragOver,

    onImagesLoaded,
    imagesDownloaded,
  };
};
